/**
 * EntranceExitLogs Component
 * 입출입 기록 UI
 *
 * 환경변수 REACT_APP_USE_REALTIME에 따라 자동 전환:
 * - true: Realtime (WebSocket) 사용
 * - false: Polling 방식 사용 (안전한 기본값)
 *
 * 주: `config/supabase` 모듈은 레거시 이름이며 현재는 단순 환경변수 게이트로만 사용됨.
 * 실제 통신은 자체 백엔드 API/WebSocket 경유. (NewAttend는 2026-02-02 Supabase 폐기 완료)
 */
import React, { useState } from 'react';
import {
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentArrowDownIcon,
  UserPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../../config/api';
import { apiClient } from '../../../infrastructure/api';
import { useEntranceExitLogs } from '../use-cases/useEntranceExitLogs';
import { useEntranceExitLogsRealtime } from '../use-cases/useEntranceExitLogsRealtime';
import { entranceExitLogsService } from '../services/EntranceExitLogsService';
import { isRealtimeEnabled } from '../../../config/supabase';

const EntranceExitLogs: React.FC = () => {
  // Realtime 설정에 따라 모드 결정 (Hook 호출 전에 결정)
  // (isRealtimeEnabled의 모듈 경로는 레거시 이름이며 실제 Supabase와 무관)
  const useRealtime = isRealtimeEnabled();

  // React Hook 규칙: 모든 Hook을 항상 같은 순서로 호출
  // enabled 파라미터로 실제 실행 여부 제어 (동시 실행 방지)
  const pollingResult = useEntranceExitLogs(!useRealtime);
  const realtimeResult = useEntranceExitLogsRealtime(useRealtime);

  // 설정에 따라 결과 선택
  const hookResult = useRealtime ? realtimeResult : pollingResult;

  const {
    logs,
    loading,
    isRealtimeConnected,
    searchName,
    actionFilter,
    currentPage,
    totalLogs,
    totalPages,
    itemsPerPage,
    filter,
    handleSearchChange,
    handleActionFilterChange,
    handleResetFilters,
    handlePageChange,
    handlePreviousPage,
    handleNextPage,
    handleExportToExcel,
    handleCustomDateRangeChange,
  } = hookResult;

  // 날짜 필터 여부 확인
  const hasDateFilter = filter?.startDate && filter?.endDate;

  // Realtime 모드 표시 (개발자용)
  const realtimeMode = 'realtimeMode' in hookResult ? hookResult.realtimeMode : 'polling';

  // 강제 입실 관련 state
  const [showForceCheckinModal, setShowForceCheckinModal] = useState(false);
  const [availableMembers, setAvailableMembers] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [forceCheckinLoading, setForceCheckinLoading] = useState(false);
  const [sendNotification, setSendNotification] = useState(true);
  const [availableSeats, setAvailableSeats] = useState<any[]>([]);
  const [selectedSeatId, setSelectedSeatId] = useState<string | null>(null);
  const [loadingSeats, setLoadingSeats] = useState(false);
  const [checkinTime, setCheckinTime] = useState('');
  const [checkinDate, setCheckinDate] = useState('');

  // 강제 퇴실 관련 state
  const [showForceCheckoutModal, setShowForceCheckoutModal] = useState(false);
  const [checkedInMembers, setCheckedInMembers] = useState<any[]>([]);
  const [loadingCheckoutMembers, setLoadingCheckoutMembers] = useState(false);
  const [checkoutMemberSearchTerm, setCheckoutMemberSearchTerm] = useState('');
  const [selectedCheckoutMember, setSelectedCheckoutMember] = useState<any>(null);
  const [forceCheckoutLoading, setForceCheckoutLoading] = useState(false);
  const [sendCheckoutNotification, setSendCheckoutNotification] = useState(true);
  const [checkoutTime, setCheckoutTime] = useState('');
  const [checkoutDate, setCheckoutDate] = useState('');

  // 기록 수정 관련 state (2026-09-02 부천신중동점 요청)
  const [editingLogId, setEditingLogId] = useState<string | null>(null);
  const [editTime, setEditTime] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  // 얼굴을 잘못 알아본 기록의 '주인'을 바로잡기
  const [ownerEditLogId, setOwnerEditLogId] = useState<string | null>(null);
  const [ownerEditSearch, setOwnerEditSearch] = useState('');
  const [ownerEditCandidates, setOwnerEditCandidates] = useState<any[]>([]);

  // 주인 수정 모드 열기 — 학생 목록을 받아 고를 수 있게 한다
  const openOwnerEdit = async (logId: string) => {
    setOwnerEditLogId(logId);
    setOwnerEditSearch('');
    try {
      const response = await apiClient.get('/v1/owner/members-for-checkin');
      setOwnerEditCandidates(response.data.members || []);
    } catch (error) {
      console.error('[기록주인수정] 학생 목록 로드 오류:', error);
      setOwnerEditCandidates([]);
    }
  };

  // 기록의 주인을 실제 그 학생으로 바로잡기
  const handleEditLogOwner = async (logId: string, newUserId: string, newName: string) => {
    setEditSaving(true);
    try {
      const response = await apiClient.patch(
        `/v1/owner/entrance-exit/logs/${logId}/owner`,
        { new_user_id: newUserId, reason: '얼굴 인식 오류 정정' }
      );
      const notes = (response.data.data?.notes || []).join('\n· ');
      alert(`${response.data.message}\n\n· ${notes}`);
      setOwnerEditLogId(null);
      window.location.reload();
    } catch (error: any) {
      console.error('[기록주인수정] 오류:', error);
      alert(error.response?.data?.detail || '수정에 실패했습니다.');
    } finally {
      setEditSaving(false);
    }
  };

  // 입·퇴실 기록 시각 바로잡기 (2026-09-05: 날짜까지 바로잡을 수 있게 확장)
  const handleEditLogTime = async (logId: string) => {
    if (!editTime) {
      alert('바로잡을 시각을 입력해주세요.');
      return;
    }
    if (editDate) {
      const at = buildLocalDateTime(editDate, editTime);
      if (!at) {
        alert('날짜와 시각을 다시 확인해주세요.');
        return;
      }
      if (isFutureMoment(at)) {
        alert('아직 오지 않은 시각으로는 바꿀 수 없습니다.');
        return;
      }
    }
    setEditSaving(true);
    try {
      const response = await apiClient.patch(
        `/v1/owner/entrance-exit/logs/${logId}/time`,
        { new_time: editTime, new_date: editDate || undefined }
      );
      alert(response.data.message || '기록을 바로잡았습니다.');
      setEditingLogId(null);
      window.location.reload();
    } catch (error: any) {
      console.error('[기록수정] 오류:', error);
      alert(error.response?.data?.detail || '수정에 실패했습니다.');
    } finally {
      setEditSaving(false);
    }
  };

  // API Base URL 결정 (HTTPS에서는 nginx 프록시 사용)
  const getApiUrl = () => {
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      return ''; // nginx 프록시 사용
    }
    return API_BASE_URL;
  };

  // 강제 입실 가능한 회원 목록 조회
  const loadAvailableMembers = async () => {
    setLoadingMembers(true);
    try {
      const response = await apiClient.get('/v1/owner/members-for-checkin');
      setAvailableMembers(response.data.members || []);
    } catch (error) {
      console.error('[강제입실] 회원 목록 로드 오류:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  // 빈 좌석 목록 조회
  const loadAvailableSeats = async () => {
    setLoadingSeats(true);
    try {
      const response = await apiClient.get('/v1/seats/status');
      // available 상태인 좌석만 필터링
      const emptySeats = (response.data.seats || []).filter((seat: any) => seat.status === 'available');
      setAvailableSeats(emptySeats);
    } catch (error) {
      console.error('[강제입실] 좌석 목록 로드 오류:', error);
    } finally {
      setLoadingSeats(false);
    }
  };

  // 현재 시간을 HH:MM 형식으로
  const getNowTimeString = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  };

  // 오늘 날짜를 YYYY-MM-DD 형식으로
  const getTodayDateString = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  /**
   * 날짜 + 시각을 하나의 시각으로 합친다.
   *
   * 🔴 2026-09-05 우장산점 사고 — 예전엔 날짜 칸이 아예 없어 항상 '버튼 누른 날'로 저장됐다.
   *    원장님이 새벽 00:25 에 전날(9/4) 16:36 퇴실을 넣었더니 9/5 16:36, 즉 16시간
   *    **미래**로 기록됐다. 상태 보정 작업이 30분마다 '가장 늦은 기록'을 현재 상태로
   *    삼기 때문에, 그 학생은 오늘 등원해도 계속 퇴실로 뒤집혔다.
   *    → ① 날짜를 직접 고르게 하고 ② 미래 시각은 아예 막는다(아래 isFutureMoment).
   */
  const buildLocalDateTime = (dateStr: string, timeStr: string): Date | null => {
    if (!dateStr || !timeStr) return null;
    const dt = new Date(`${dateStr}T${timeStr}:00`);
    return isNaN(dt.getTime()) ? null : dt;
  };

  // 미래 여부 — PC 시계가 조금 빠를 수 있어 1분은 봐준다
  const isFutureMoment = (dt: Date) => dt.getTime() > Date.now() + 60 * 1000;

  // 강제 입실 모달 열기
  const openForceCheckinModal = () => {
    setShowForceCheckinModal(true);
    setSelectedMember(null);
    setMemberSearchTerm('');
    setSelectedSeatId(null);
    setCheckinTime(getNowTimeString());
    setCheckinDate(getTodayDateString());
    loadAvailableMembers();
    loadAvailableSeats();
  };

  // 강제 입실 처리
  const handleForceCheckin = async () => {
    if (!selectedMember) {
      alert('입실할 회원을 선택해주세요.');
      return;
    }

    // 좌석 미배정 회원인 경우 좌석 선택 필수
    if (!selectedMember.assigned_seat && !selectedSeatId) {
      alert('좌석을 선택해주세요.');
      return;
    }

    setForceCheckinLoading(true);
    try {
      // 요청 데이터 구성 (좌석 미배정시 선택한 좌석 ID 전달)
      const requestBody: any = {
        user_id: selectedMember.id,
        send_notification: sendNotification,
      };

      if (!selectedMember.assigned_seat && selectedSeatId) {
        requestBody.seat_id = selectedSeatId;
      }

      // 시각 지정 — 날짜까지 원장님이 고른 값을 쓴다 (2026-09-05)
      if (checkinTime) {
        const at = buildLocalDateTime(checkinDate || getTodayDateString(), checkinTime);
        if (!at) {
          alert('입실 날짜와 시각을 다시 확인해주세요.');
          return;
        }
        if (isFutureMoment(at)) {
          alert('아직 오지 않은 시각으로는 입실 처리를 할 수 없습니다.\n날짜와 시각을 확인해주세요.');
          return;
        }
        requestBody.custom_time = at.toISOString();
      }

      const response = await apiClient.post('/v1/owner/force-checkin', requestBody);
      const result = response.data;

      if (result.success) {
        alert(`${selectedMember.name}님이 좌석 ${result.seat_number}번에 입실 처리되었습니다.${result.notification_sent ? '\n알림톡이 발송되었습니다.' : ''}`);
        setShowForceCheckinModal(false);
        setSelectedMember(null);
        // 목록 새로고침
        window.location.reload();
      } else {
        alert(`입실 처리 실패: ${result.detail || result.message || '알 수 없는 오류'}`);
      }
    } catch (error: any) {
      console.error('강제 입실 오류:', error);
      alert(`입실 처리 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    } finally {
      setForceCheckinLoading(false);
    }
  };

  // 회원 필터링 (입실용)
  const filteredMembers = availableMembers.filter(member =>
    member.name?.toLowerCase().includes(memberSearchTerm.toLowerCase()) ||
    member.phone?.includes(memberSearchTerm) ||
    member.user_number?.toLowerCase().includes(memberSearchTerm.toLowerCase())
  );

  // 강제 퇴실 가능한 회원 목록 조회
  const loadCheckedInMembers = async () => {
    setLoadingCheckoutMembers(true);
    try {
      const response = await apiClient.get('/v1/owner/members-for-checkout');
      setCheckedInMembers(response.data.members || []);
    } catch (error) {
      console.error('[강제퇴실] 회원 목록 로드 오류:', error);
    } finally {
      setLoadingCheckoutMembers(false);
    }
  };

  // 강제 퇴실 모달 열기
  const openForceCheckoutModal = () => {
    setShowForceCheckoutModal(true);
    setSelectedCheckoutMember(null);
    setCheckoutMemberSearchTerm('');
    setCheckoutTime(getNowTimeString());
    setCheckoutDate(getTodayDateString());
    loadCheckedInMembers();
  };

  // 강제 퇴실 처리
  const handleForceCheckout = async () => {
    if (!selectedCheckoutMember) {
      alert('퇴실할 회원을 선택해주세요.');
      return;
    }

    setForceCheckoutLoading(true);
    try {
      const requestBody: any = {
        user_id: selectedCheckoutMember.id,
        send_notification: sendCheckoutNotification,
      };

      // 시각 지정 — 날짜까지 원장님이 고른 값을 쓴다 (2026-09-05)
      if (checkoutTime) {
        const at = buildLocalDateTime(checkoutDate || getTodayDateString(), checkoutTime);
        if (!at) {
          alert('퇴실 날짜와 시각을 다시 확인해주세요.');
          return;
        }
        if (isFutureMoment(at)) {
          alert('아직 오지 않은 시각으로는 퇴실 처리를 할 수 없습니다.\n날짜와 시각을 확인해주세요.');
          return;
        }
        requestBody.custom_time = at.toISOString();
      }

      const response = await apiClient.post('/v1/owner/force-checkout', requestBody);
      const result = response.data;

      if (result.success) {
        alert(`${selectedCheckoutMember.name}님이 퇴실 처리되었습니다.\n이용시간: ${result.duration_minutes}분${result.notification_sent ? '\n알림톡이 발송되었습니다.' : ''}`);
        setShowForceCheckoutModal(false);
        setSelectedCheckoutMember(null);
        // 목록 새로고침
        window.location.reload();
      } else {
        alert(`퇴실 처리 실패: ${result.detail || result.message || '알 수 없는 오류'}`);
      }
    } catch (error: any) {
      console.error('강제 퇴실 오류:', error);
      alert(`퇴실 처리 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    } finally {
      setForceCheckoutLoading(false);
    }
  };

  // 회원 필터링 (퇴실용)
  const filteredCheckoutMembers = checkedInMembers.filter(member =>
    member.name?.toLowerCase().includes(checkoutMemberSearchTerm.toLowerCase()) ||
    String(member.seat_number)?.includes(checkoutMemberSearchTerm)
  );

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-gray-900">입출입 기록</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={openForceCheckinModal}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <UserPlusIcon className="h-5 w-5" />
              강제 입실
            </button>
            <button
              onClick={openForceCheckoutModal}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              <ArrowLeftOnRectangleIcon className="h-5 w-5" />
              강제 퇴실
            </button>
            {/* 실시간 연결 — 불빛 하나로 (출석부와 같은 기준, 2026-09-10 대표 지시) */}
            <span
              title={
                isRealtimeConnected
                  ? `실시간 연결됨 — 새 기록이 바로 나타납니다${realtimeMode === 'websocket' ? ' (WebSocket)' : ''}`
                  : '연결 끊김 — 다시 연결하는 중입니다. 잠시 새 기록이 자동으로 나타나지 않습니다'
              }
              className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${
                isRealtimeConnected ? 'bg-green-500' : 'bg-red-500 animate-pulse'
              }`}
            />
          </div>
        </div>
        <p className="text-gray-600">실시간 입장/퇴장 현황 및 기록을 확인할 수 있습니다</p>
      </div>

      {/* 필터 및 검색 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
        {/* 🔴 소제목('입출입 기록 조회')은 페이지 제목과 겹쳐 지웠고,
              '최근 30건 조회' 파란 띠는 아래 목록 머리에 이미 같은 표시가 있어 지웠다.
              엑셀 내려받기는 자주 쓰는 기능이 아니라 필터 줄 끝 작은 버튼으로 옮겼다.
              (2026-09-10 대표 지시 — 공간 절약) */}
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            {/* 시작 날짜 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                시작 날짜
              </label>
              <input
                type="date"
                value={filter?.startDate || ''}
                onChange={(e) => {
                  const startDate = e.target.value;
                  const endDate = filter?.endDate || startDate;
                  if (startDate) {
                    handleCustomDateRangeChange({ startDate, endDate });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* 종료 날짜 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                종료 날짜
              </label>
              <input
                type="date"
                value={filter?.endDate || ''}
                onChange={(e) => {
                  const endDate = e.target.value;
                  const startDate = filter?.startDate || endDate;
                  if (endDate) {
                    handleCustomDateRangeChange({ startDate, endDate });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* 회원 검색 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                회원 검색
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="회원 이름 검색..."
                  value={searchName}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* 동작 필터 + 초기화 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">동작</label>
              <div className="flex gap-2">
                <select
                  value={actionFilter}
                  onChange={(e) =>
                    handleActionFilterChange(e.target.value as 'all' | 'entry' | 'exit' | 'outing')
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">전체</option>
                  <option value="entry">입장</option>
                  <option value="exit">퇴장</option>
                  <option value="outing">외출</option>
                </select>
                <button
                  onClick={handleResetFilters}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors duration-200"
                >
                  초기화
                </button>
              </div>
            </div>
          </div>

          {/* 지금 무엇을 보고 있는지 + 엑셀 — 한 줄로 */}
          <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
            <span>
              {hasDateFilter
                ? `${filter.startDate} ~ ${filter.endDate} 기간 검색`
                : '날짜를 고르지 않으면 최근 30건만 보입니다'}
            </span>
            <button
              onClick={handleExportToExcel}
              title="지금 보이는 기록을 엑셀 파일로 내려받습니다"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 border border-green-300 rounded hover:bg-green-50 transition-colors shrink-0"
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              엑셀
            </button>
          </div>
        </div>
      </div>

      {/* 로그 테이블 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">
              입출입 기록 ({totalLogs.toLocaleString()}건)
            </h2>
            <div className="text-sm text-gray-500">
              {hasDateFilter
                ? `${filter.startDate} ~ ${filter.endDate}`
                : '최근 30건'}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  시간
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  회원명
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  동작
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  인식 방법
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  게이트
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  신뢰도
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  알림톡
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  기록 바로잡기
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                      <span className="text-gray-600 font-medium">
                        로딩 중...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                    해당 조건에 맞는 기록이 없습니다.
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entranceExitLogsService.formatTime(log.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {log.user_name}
                      </div>
                      <div className="text-sm text-gray-500">{log.user_id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${entranceExitLogsService.getActionBadgeClass(
                            log.action
                          )}`}
                        >
                          {log.action === 'entry' ? (
                            <ArrowRightOnRectangleIcon className="h-3 w-3 mr-1" />
                          ) : (
                            <ArrowLeftOnRectangleIcon className="h-3 w-3 mr-1" />
                          )}
                          {entranceExitLogsService.getActionLabel(log.action)}
                          {log.action === 'outing' && log.outing_type && (
                            <span className="ml-1">({entranceExitLogsService.getOutingTypeLabel(log.outing_type)})</span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${entranceExitLogsService.getMethodBadgeClass(
                          log.method
                        )}`}
                      >
                        {entranceExitLogsService.getMethodLabel(log.method)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.gate_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entranceExitLogsService.formatConfidence(log.confidence)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {log.notification_status === 'success' ? (
                        <span className="inline-flex h-3 w-3 rounded-full bg-green-500" title="발송 성공"></span>
                      ) : log.notification_status === 'failed' ? (
                        <span className="inline-flex h-3 w-3 rounded-full bg-red-500" title="발송 실패"></span>
                      ) : log.notification_status === 'pending' || log.notification_status === 'sending' ? (
                        <span className="inline-flex h-3 w-3 rounded-full bg-gray-800" title="발송 중"></span>
                      ) : log.notification_status === 'suppressed:bad_phone' ? (
                        // 학부모 번호 형식이 틀려 못 보냄 — 원장이 고쳐야 할 일이라 주황으로 구분 (2026-10-28)
                        <span className="inline-flex h-3 w-3 rounded-full bg-orange-400" title="보내지 않음 (학부모 번호가 잘못됨 — 회원 관리에서 고쳐 주세요)"></span>
                      ) : typeof log.notification_status === 'string' && log.notification_status.startsWith('suppressed:') ? (
                        // 일부러 안 보낸 것 — 이유를 마우스를 올리면 보여준다 (2026-10-14)
                        <span
                          className="inline-flex h-3 w-3 rounded-full bg-gray-300"
                          title={`보내지 않음 (${({
                            toilet: '화장실 다녀옴',
                            dup_entry: '연달아 입장',
                            off: '알림톡 꺼짐',
                            outing_off: '이 외출 종류는 알림 끔',
                            no_target: '받을 보호자 번호 없음',
                          } as Record<string, string>)[log.notification_status.slice(11)] || '설정에 따라'})`}
                        ></span>
                      ) : (
                        <span className="inline-flex h-3 w-3 rounded-full bg-gray-300" title="알림 미발송"></span>
                      )}
                    </td>
                    {/* 시각 수정 — 강제입실/퇴실 시각을 잘못 넣었을 때 바로잡는다.
                        확인창(window.prompt/confirm)은 환경에 따라 차단되므로 인라인 폼으로 처리 */}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {editingLogId === log.id ? (
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="date"
                            value={editDate}
                            max={getTodayDateString()}
                            onChange={(e) => setEditDate(e.target.value)}
                            className="px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                          <input
                            type="time"
                            value={editTime}
                            onChange={(e) => setEditTime(e.target.value)}
                            className="px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                          <button
                            onClick={() => handleEditLogTime(log.id)}
                            disabled={editSaving}
                            className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            {editSaving ? '저장 중' : '저장'}
                          </button>
                          <button
                            onClick={() => setEditingLogId(null)}
                            className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                          >
                            취소
                          </button>
                        </div>
                      ) : ownerEditLogId === log.id ? (
                        <div className="flex flex-col gap-1 items-center min-w-[220px]">
                          <div className="text-xs text-gray-500">
                            실제로 이 시각에 들어온 학생을 고르세요
                          </div>
                          <input
                            type="text"
                            autoFocus
                            value={ownerEditSearch}
                            onChange={(e) => setOwnerEditSearch(e.target.value)}
                            placeholder="학생 이름 검색"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                          {ownerEditSearch && (
                            <div className="w-full max-h-32 overflow-y-auto border border-gray-200 rounded">
                              {ownerEditCandidates
                                .filter((m) => m.name?.includes(ownerEditSearch))
                                .slice(0, 8)
                                .map((m) => (
                                  <button
                                    key={m.id}
                                    disabled={editSaving}
                                    onClick={() => handleEditLogOwner(log.id, m.id, m.name)}
                                    className="block w-full px-2 py-1 text-sm text-left hover:bg-blue-50 disabled:opacity-50"
                                  >
                                    {m.name}
                                    <span className="ml-1 text-xs text-gray-500">{m.user_number}</span>
                                  </button>
                                ))}
                            </div>
                          )}
                          <button
                            onClick={() => setOwnerEditLogId(null)}
                            className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                          >
                            취소
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => {
                              setEditingLogId(log.id);
                              // formatTime 은 "YYYY-MM-DD HH:MM:SS" 를 준다.
                              // 앞에서 자르면 "2026-" 이 들어가 시간 칸이 깨진다 — 시각 부분만 뽑는다.
                              setEditTime(
                                (entranceExitLogsService.formatTime(log.timestamp).split(' ')[1] || '').slice(0, 5)
                              );
                              // 날짜도 같이 — 날짜가 잘못 찍힌 기록을 바로잡으려면 이게 있어야 한다 (2026-09-05)
                              setEditDate(
                                (entranceExitLogsService.formatTime(log.timestamp).split(' ')[0] || '')
                              );
                            }}
                            className="px-2 py-1 text-xs font-medium text-blue-600 border border-blue-200 rounded hover:bg-blue-50"
                          >
                            시각
                          </button>
                          <button
                            onClick={() => openOwnerEdit(log.id)}
                            title="얼굴을 잘못 알아봐서 다른 학생으로 기록된 경우"
                            className="px-2 py-1 text-xs font-medium text-orange-600 border border-orange-200 rounded hover:bg-orange-50"
                          >
                            다른 사람
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {entranceExitLogsService.calculatePageRange(
                  currentPage,
                  itemsPerPage,
                  totalLogs
                )}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePreviousPage()}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>

                <div className="flex space-x-1">
                  {entranceExitLogsService
                    .generatePageNumbers(currentPage, totalPages)
                    .map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1 text-sm font-medium rounded-md ${
                          page === currentPage
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                </div>

                <button
                  onClick={() => handleNextPage()}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 강제 입실 모달 */}
      {showForceCheckinModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold text-gray-900">강제 입실</h2>
              <button
                onClick={() => setShowForceCheckinModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-4">
              {/* 회원 검색 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  회원 검색
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={memberSearchTerm}
                    onChange={(e) => setMemberSearchTerm(e.target.value)}
                    placeholder="이름, 전화번호, 회원번호로 검색"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* 회원 목록 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  입실 가능한 회원 ({filteredMembers.length}명)
                </label>
                <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                  {loadingMembers ? (
                    <div className="p-4 text-center text-gray-500">
                      회원 목록 로딩 중...
                    </div>
                  ) : filteredMembers.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      {memberSearchTerm ? '검색 결과가 없습니다.' : '입실 가능한 회원이 없습니다.'}
                    </div>
                  ) : (
                    filteredMembers.map((member) => (
                      <div
                        key={member.id}
                        onClick={() => setSelectedMember(member)}
                        className={`p-3 cursor-pointer border-b last:border-b-0 transition-colors ${
                          selectedMember?.id === member.id
                            ? 'bg-blue-50 border-blue-200'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">
                              {member.name}
                              <span className="ml-2 text-xs text-gray-500">
                                {member.user_number}
                              </span>
                              {/* 처음 오는 학생인지, 나갔다 다시 오는 학생인지 구분해서 보이게
                                  (부천신중동점 요청, 2026-09-02) */}
                              {member.current_state === 'outing' && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  외출 중
                                  {member.outing_type ? ` · ${entranceExitLogsService.getOutingTypeLabel(member.outing_type)}` : ''}
                                  {member.state_since ? ` ${member.state_since}~` : ''}
                                </span>
                              )}
                              {member.current_state === 'exited' && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                  퇴실{member.state_since ? ` ${member.state_since}` : ''} · 재입실
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{member.phone}</div>
                          </div>
                          <div className="text-right">
                            {member.assigned_seat ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                좌석 {member.assigned_seat.seat_number}번
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                좌석 미배정
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* 선택된 회원 정보 */}
              {selectedMember && (
                <div className={`mb-4 p-3 rounded-lg ${selectedMember.assigned_seat || selectedSeatId ? 'bg-blue-50' : 'bg-yellow-50'}`}>
                  <div className={`text-sm ${selectedMember.assigned_seat || selectedSeatId ? 'text-blue-800' : 'text-yellow-800'}`}>
                    <strong>{selectedMember.name}</strong>님을
                    {selectedMember.assigned_seat ? (
                      <span className="font-bold"> 좌석 {selectedMember.assigned_seat.seat_number}번</span>
                    ) : selectedSeatId ? (
                      <span className="font-bold"> 좌석 {availableSeats.find(s => s.id === selectedSeatId)?.seat_number || selectedSeatId}번</span>
                    ) : (
                      <span className="font-bold"> (좌석 선택 필요)</span>
                    )}
                    에 입실 처리합니다.
                  </div>
                </div>
              )}

              {/* 좌석 미배정 회원일 경우 좌석 선택 UI */}
              {selectedMember && !selectedMember.assigned_seat && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    입실할 좌석 선택 <span className="text-red-500">*</span>
                  </label>
                  {loadingSeats ? (
                    <div className="p-3 text-center text-gray-500">좌석 목록 로딩 중...</div>
                  ) : availableSeats.length === 0 ? (
                    <div className="p-3 text-center text-red-500 bg-red-50 rounded-lg">
                      빈 좌석이 없습니다
                    </div>
                  ) : (
                    <select
                      value={selectedSeatId || ''}
                      // 🔴 좌석 PK 는 2026-05-25 부터 UUID(문자)다. Number() 로 감싸면 NaN 이 되어
                      //    "좌석을 선택하세요" 경고가 안 사라지고 강제 입실이 막힌다.
                      onChange={(e) => setSelectedSeatId(e.target.value || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">좌석을 선택하세요</option>
                      {availableSeats
                        .sort((a, b) => a.seat_number - b.seat_number)
                        .map((seat) => (
                          <option key={seat.id} value={seat.id}>
                            {seat.seat_number}번 좌석
                          </option>
                        ))}
                    </select>
                  )}
                </div>
              )}

              {/* 알림톡 발송 옵션 */}
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendNotification}
                    onChange={(e) => setSendNotification(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">학부모에게 알림톡 발송</span>
                </label>
              </div>

              {/* 입실 날짜·시간 지정 — 날짜 칸이 없어 늘 '오늘'로 저장되던 문제 수정 (2026-09-05) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">입실 날짜 · 시간</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={checkinDate}
                    max={getTodayDateString()}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCheckinDate(v);
                      // 지난 날짜를 뒤늦게 정리하는 것이라면 학부모 알림은 기본으로 끈다 —
                      // 어제 일인데 지금 "입장했습니다"가 가면 학부모가 놀란다. 필요하면 다시 켜면 된다.
                      if (v && v !== getTodayDateString()) setSendNotification(false);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  <input
                    type="time"
                    value={checkinTime}
                    onChange={(e) => setCheckinTime(e.target.value)}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  기본값: 오늘 · 현재 시간. <span className="font-medium">지난 날짜도 고를 수 있습니다.</span> 아직 오지 않은 시각은 넣을 수 없습니다.
                </p>
                {checkinDate && checkinDate !== getTodayDateString() && (
                  <p className="mt-1 text-xs text-amber-700">
                    지난 날짜를 정리하는 중이라 학부모 알림톡은 꺼 두었습니다. 보내시려면 위에서 다시 켜주세요.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowForceCheckinModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleForceCheckin}
                disabled={!selectedMember || (!selectedMember.assigned_seat && !selectedSeatId) || forceCheckinLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {forceCheckinLoading ? '처리 중...' : '입실 처리'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 강제 퇴실 모달 */}
      {showForceCheckoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">강제 퇴실</h3>
              <button
                onClick={() => setShowForceCheckoutModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4">
              {/* 검색 */}
              <div className="mb-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="회원명 또는 좌석번호 검색..."
                    value={checkoutMemberSearchTerm}
                    onChange={(e) => setCheckoutMemberSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              </div>

              {/* 회원 목록 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  현재 입실 중인 회원 ({filteredCheckoutMembers.length}명)
                </label>
                <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                  {loadingCheckoutMembers ? (
                    <div className="p-4 text-center text-gray-500">
                      회원 목록 로딩 중...
                    </div>
                  ) : filteredCheckoutMembers.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      {checkoutMemberSearchTerm ? '검색 결과가 없습니다.' : '현재 입실 중인 회원이 없습니다.'}
                    </div>
                  ) : (
                    filteredCheckoutMembers.map((member) => (
                      <div
                        key={member.id}
                        onClick={() => setSelectedCheckoutMember(member)}
                        className={`p-3 cursor-pointer border-b last:border-b-0 transition-colors ${
                          selectedCheckoutMember?.id === member.id
                            ? 'bg-red-50 border-red-200'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">
                              {member.name}
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                좌석 {member.seat_number}번
                              </span>
                            </div>
                            <div className="text-sm text-gray-500">
                              입실: {member.check_in_time} ({member.duration_minutes}분 이용중)
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* 선택된 회원 정보 */}
              {selectedCheckoutMember && (
                <div className="mb-4 p-3 bg-red-50 rounded-lg">
                  <div className="text-sm text-red-800">
                    <strong>{selectedCheckoutMember.name}</strong>님을
                    <span className="font-bold"> 좌석 {selectedCheckoutMember.seat_number}번</span>에서
                    퇴실 처리합니다.
                    <div className="text-xs mt-1 text-red-600">
                      이용시간: {selectedCheckoutMember.duration_minutes}분
                    </div>
                  </div>
                </div>
              )}

              {/* 알림톡 발송 옵션 */}
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendCheckoutNotification}
                    onChange={(e) => setSendCheckoutNotification(e.target.checked)}
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">학부모에게 알림톡 발송</span>
                </label>
              </div>

              {/* 퇴실 날짜·시간 지정 — 날짜 칸이 없어 늘 '오늘'로 저장되던 문제 수정 (2026-09-05 우장산점) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">퇴실 날짜 · 시간</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={checkoutDate}
                    max={getTodayDateString()}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCheckoutDate(v);
                      // 지난 날짜 정리 시 학부모 알림 기본 끔 (위 입실 칸과 같은 이유)
                      if (v && v !== getTodayDateString()) setSendCheckoutNotification(false);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500 text-sm"
                  />
                  <input
                    type="time"
                    value={checkoutTime}
                    onChange={(e) => setCheckoutTime(e.target.value)}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500 text-sm"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  기본값: 오늘 · 현재 시간. <span className="font-medium">어제 퇴실을 오늘 처리하실 땐 날짜를 어제로 바꿔주세요.</span> 아직 오지 않은 시각은 넣을 수 없습니다.
                </p>
                {checkoutDate && checkoutDate !== getTodayDateString() && (
                  <p className="mt-1 text-xs text-amber-700">
                    지난 날짜를 정리하는 중이라 학부모 알림톡은 꺼 두었습니다. 보내시려면 위에서 다시 켜주세요.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowForceCheckoutModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleForceCheckout}
                disabled={!selectedCheckoutMember || forceCheckoutLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {forceCheckoutLoading ? '처리 중...' : '퇴실 처리'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntranceExitLogs;
